import { observer } from 'mobx-react-lite'
import { useCallback, useEffect, useState } from 'react'
import { Platform, StyleSheet } from 'react-native'

import { DS } from '../../../../../design-system'
import { useStore } from '../../../../../models'
import { events } from '../../../../../models/analytics/analytics-events'
import { TwoCards } from '../../../../TwoCards'
import { ButtonEmoji } from '../../../ButtonEmoji'
import { TimerShowCards } from './TimerShowCards'
import { BottomSheetView } from '@gorhom/bottom-sheet'
import { useCommandsContext, useCommonCommands } from '../../../../../screens/new-design/hooks/useCommonCommands'
import { useGameState } from '../../../../../screens/GameContainer/useGameState'

const EMOJIS = ['👑', '👏', '🙌', '🔥', '💃🏻']

export interface ShowCardsProps {
  onClose: () => void
  timeToKeepOpen: number
}

export const ShowCards = observer(({ onClose, timeToKeepOpen }: ShowCardsProps) => {
  const context = useCommandsContext()
  const { myPlayer: myPlayerConvex } = useGameState()
  const {
    game: { gameId, myPlayer, gameKind, isTournamentGameOver, isGameReset },
    analytics: { setEvent }
  } = useStore()
  const cards = 'convex' === context ? myPlayerConvex?.cards : myPlayer?.cards
  // Spectators, busted players, and mid-hand late-joiners have no hole cards
  // to reveal. In those cases we still render the sheet so they can see the
  // celebration emojis + next-hand timer, but the cards section is hidden.
  const hasCards = Array.isArray(cards) && cards.length >= 2 && cards[0] && cards[1]

  const [shownCards, setShownCards] = useState({ left: false, right: false })

  useEffect(() => {
    if (!hasCards) return
    setEvent(events.gameOverScreen.show_cards_drawer_viewed, {
      game_id: gameId,
      game_kind: gameKind
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasCards])

  const sendShowCardsCommand = useCommonCommands('onSendShowCards', { isGameOverScreen: false })
  const onSendShowCards = useCallback((leftCardShown: boolean, rightCardShown: boolean) => {
    sendShowCardsCommand(leftCardShown, rightCardShown)
    setShownCards({ left: leftCardShown, right: rightCardShown })
    onClose()
  }, [])
  const onSendCelebration = useCommonCommands('onSendCelebration', { isGameOverScreen: false })

  useEffect(() => {
    if (isGameReset) {
      onClose()
    }
  }, [isGameReset, onClose])

  return (
    <BottomSheetView style={Platform.OS !== 'web' ? { flex: 1 } : {}}>
      <DS.Frame style={[styles.container, Platform.select({ ios: { marginTop: DS.spacing['16'] } })]} gap={'8'}>
        {hasCards ? (
          <>
            <DS.Text variant="Heading 4/Regular" textAlign="center">
              Show Cards?
            </DS.Text>
            <DS.Frame row justifyContent="space-between">
              <TwoCards
                opacity={shownCards.left && !shownCards.right}
                cards={[cards?.[0], undefined]}
                onSend={onSendShowCards}
              />
              <TwoCards opacity={shownCards.left && shownCards.right} cards={cards} onSend={onSendShowCards} />
              <TwoCards
                opacity={!shownCards.left && shownCards.right}
                cards={[undefined, cards?.[1]]}
                onSend={onSendShowCards}
              />
            </DS.Frame>
          </>
        ) : null}
        <DS.Frame style={styles.roundedContainer} gap={'8'}>
          <DS.Frame row justifyContent="center" gap="8">
            {EMOJIS.map((e, idx) => (
              <ButtonEmoji key={`winner-emoji-${idx}`} emoji={e} onPress={onSendCelebration} />
            ))}
          </DS.Frame>
          <DS.Text variant="Body Small/Regular" textAlign="center">
            Applaud the winner
          </DS.Text>
        </DS.Frame>

        {timeToKeepOpen ? (
          <DS.Frame style={styles.timerContainer}>
            <TimerShowCards showNextHandIn={!isTournamentGameOver} durationSeconds={timeToKeepOpen} isShowCards />
          </DS.Frame>
        ) : null}
      </DS.Frame>
    </BottomSheetView>
  )
})

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: DS.spacing['16']
  },
  roundedContainer: {
    paddingHorizontal: DS.spacing['12'],
    paddingVertical: DS.spacing['8'],
    borderRadius: 96,
    borderWidth: 1,
    borderColor: DS.palette['Brand/Light Grey']
  },
  timerContainer: {
    marginVertical: DS.spacing['8']
  }
})
